/**
 * WP-V2-PK — katalog obiektów Parku (PLAN-V2 §6 Park).
 * WAVE7-C: placementy zsynchronizowane z bg_park (zjeżdżalnia / huśtawka / ławka).
 * Interakcje MP: tylko piłka (sync NetClient). Reszta = lokalna wizualnie.
 * Zero nowych pól ParkState.
 */
import type { ObjectDef, SpawnPlacement } from '../../interact/index.js';
import type { ColliderRect } from '../../core/world/index.js';
import { GAME_WIDTH, GAME_HEIGHT } from '@pieskowo/shared';

/** Świat = viewport serwera (§4.2 / §6.2) — 1280×720. */
export const PARK_WORLD_W = GAME_WIDTH;
export const PARK_WORLD_H = GAME_HEIGHT;

export const PARK_SWING_DEF_ID = 'park_swing';
export const PARK_SLIDE_DEF_ID = 'park_slide';
export const PARK_SANDBOX_DEF_ID = 'park_sandbox';
export const PARK_SANDCAKE_DEF_ID = 'park_sandcake';
export const PARK_BENCH_DEF_ID = 'park_bench';

/**
 * Pozycje sprzętu dopasowane do ilustracji bg_park (1280×720).
 * Hit-zone leży na malunku z tła — bez „protezy” obok.
 */
export const PARK_BG_ANCHORS = {
  slide: { x: 281, y: 345 },
  swing: { x: 640, y: 302 },
  sandbox: { x: 486, y: 430 },
  ball: { x: 870, y: 420 },
  bench: { x: 1024, y: 400 },
  pond: { x: 153, y: 518 },
} as const;

/** Definicje typów. Hit-boxy ≥64 px; slide/swing większe pod tło. */
export const PARK_OBJECT_DEFS: ObjectDef[] = [
  {
    id: PARK_SWING_DEF_ID,
    texture: 'obj_park_swing',
    size: 160,
    interactions: ['tap'],
    sound: 'swing',
    fx: 'sparkle',
    dogReaction: 'happy',
  },
  {
    id: PARK_SLIDE_DEF_ID,
    texture: 'obj_park_slide',
    size: 170,
    interactions: ['tap'],
    sound: 'whoosh',
    fx: 'stars',
    dogReaction: 'jump',
  },
  {
    id: PARK_SANDBOX_DEF_ID,
    texture: 'obj_park_sandbox',
    size: 120,
    interactions: ['tap'],
    sound: 'sand',
    fx: 'crumbs',
    dogReaction: 'sniff',
  },
  {
    id: PARK_SANDCAKE_DEF_ID,
    texture: 'obj_park_sandcake',
    size: 72,
    // Bez 'pickup': babka nie wpada w pyszczek na stałe.
    interactions: ['tap', 'drag'],
    fx: 'sparkle',
    dogReaction: 'happy',
  },
  {
    id: PARK_BENCH_DEF_ID,
    texture: 'obj_park_bench',
    size: 140,
    interactions: ['tap'],
    sound: 'sit',
    fx: 'hearts',
    dogReaction: 'happy',
  },
];

/** Stałe rozmieszczenie placu — kotwice bg_park. */
export const PARK_PLACEMENTS: SpawnPlacement[] = [
  { defId: PARK_SLIDE_DEF_ID, x: PARK_BG_ANCHORS.slide.x, y: PARK_BG_ANCHORS.slide.y },
  { defId: PARK_SWING_DEF_ID, x: PARK_BG_ANCHORS.swing.x, y: PARK_BG_ANCHORS.swing.y },
  { defId: PARK_SANDBOX_DEF_ID, x: PARK_BG_ANCHORS.sandbox.x, y: PARK_BG_ANCHORS.sandbox.y },
  { defId: PARK_BENCH_DEF_ID, x: PARK_BG_ANCHORS.bench.x, y: PARK_BG_ANCHORS.bench.y },
];

/** Lekkie blokady wokół sprzętu (ścieżka wolna). */
export const PARK_COLLIDERS: ColliderRect[] = [
  // belka huśtawki (góra ramy)
  { x: PARK_BG_ANCHORS.swing.x - 70, y: PARK_BG_ANCHORS.swing.y - 90, width: 140, height: 20 },
  // platforma zjeżdżalni
  { x: PARK_BG_ANCHORS.slide.x - 70, y: PARK_BG_ANCHORS.slide.y - 50, width: 50, height: 90 },
];

/** Oczekiwane klucze tekstur placu (PNG w /assets/real lub procedural). */
export const PARK_PLAYGROUND_TEXTURES: readonly string[] = [
  'obj_park_swing',
  'obj_park_slide',
  'obj_park_sandbox',
  'obj_park_sandcake',
  'obj_park_bench',
] as const;

/**
 * Historycznie brakowało PNG — slide/swing/sandbox/sandcake są w /assets/real.
 * Ławka = procedural (playgroundVisuals).
 */
export const PARK_MISSING_ASSETS: readonly string[] = [] as const;

export function isPlaygroundRide(defId: string): boolean {
  return defId === PARK_SWING_DEF_ID || defId === PARK_SLIDE_DEF_ID;
}

export function isSandbox(defId: string): boolean {
  return defId === PARK_SANDBOX_DEF_ID;
}

export function isParkBench(defId: string): boolean {
  return defId === PARK_BENCH_DEF_ID;
}

/** Offset babki w piaskownicy (deterministyczny indeks). */
export function sandcakePlacement(
  index: number,
  baseX = PARK_BG_ANCHORS.sandbox.x,
  baseY = PARK_BG_ANCHORS.sandbox.y,
): SpawnPlacement {
  const ring = [
    { dx: -28, dy: -10 },
    { dx: 24, dy: -6 },
    { dx: -8, dy: 18 },
    { dx: 20, dy: 16 },
    { dx: 0, dy: -22 },
    { dx: -32, dy: 14 },
  ];
  const o = ring[index % ring.length]!;
  return {
    defId: PARK_SANDCAKE_DEF_ID,
    x: baseX + o.dx,
    y: baseY + o.dy,
  };
}

export const MAX_LOCAL_SANDCAKES = 6;
